import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Handshake, Megaphone, Network, Building2, Radar } from "lucide-react";
import { Reveal, Tilt } from "@/components/Motion";
import { OpportunityCard } from "@/components/OpportunityCard";
import { EmptyState } from "@/components/EmptyState";
import { useFetch, useSeo } from "@/lib/hooks";
import { imgSources } from "@/lib/api";
import { useMode } from "@/context/ModeContext";

function Hero() {
  const ref = useRef(null);
  const { mode } = useMode();
  useEffect(() => {
    if (mode !== "premium" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = Math.min(window.scrollY, 900);
        ref.current?.querySelectorAll("[data-parallax]").forEach((el) => {
          el.style.transform = `translate3d(0, ${y * parseFloat(el.dataset.parallax)}px, 0)`;
        });
      });
    };
    window.addEventListener("scroll", on, { passive: true });
    return () => { window.removeEventListener("scroll", on); cancelAnimationFrame(raf); };
  }, [mode]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative min-h-[100svh] overflow-hidden pt-28 grain">
      <div className="pointer-events-none absolute inset-0 grid-floor" />
      <div className="beam beam-red beam-anim fx-only left-[8%]" />
      <div className="beam beam-red beam-anim fx-only left-[14%] opacity-40" style={{ animationDelay: "-2s" }} />
      <div className="beam beam-blue beam-anim fx-only left-[70%]" style={{ animationDelay: "-5s" }} />
      <div className="beam beam-blue beam-anim fx-only left-[86%] opacity-50" style={{ animationDelay: "-7s" }} />
      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] glow-red" />
      <div className="pointer-events-none absolute -right-40 top-0 h-[560px] w-[560px] glow-blue" />

      <div className="wrap relative grid items-center gap-14 pb-20 pt-8 lg:grid-cols-12 lg:pt-16">
        <div className="lg:col-span-6">
          <p className="eyebrow">Partner commerciale · Opportunità tramite partner</p>
          <h1 className="mt-6 text-4xl font-extrabold leading-[1.02] text-white sm:text-5xl lg:text-6xl">
            Le opportunità giuste.<br /><span className="text-gradient">Le connessioni che contano.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed muted md:text-lg">
            PUBBLICELLI collabora con aziende e imprese per valorizzare opportunità e mettere in contatto chi cerca con chi propone.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#occasioni" className="btn btn-red px-7 py-3.5" data-testid="hero-discover-btn">Scopri le occasioni<ArrowRight size={16} /></a>
            <Link to="/segnala-cosa-cerchi" className="btn btn-ghost px-7 py-3.5" data-testid="hero-segnala-btn">Segnala cosa cerchi</Link>
          </div>
          <ul className="mt-12 grid max-w-lg grid-cols-3 gap-4 text-xs muted">
            {["Case e ville", "Terreni", "Attività e locali"].map((t, i) => (
              <li key={t} className="border-l-2 pl-3" style={{ borderColor: i === 1 ? "var(--blu)" : "var(--rosso)" }}>{t}</li>
            ))}
          </ul>
        </div>

        <div className="relative lg:col-span-6" style={{ perspective: "1400px" }}>
          <div data-parallax="0.06" className="relative">
            <div className="chrome relative overflow-hidden rounded-[28px] shadow-[0_60px_120px_-40px_rgba(7,91,255,.55)]" style={{ transform: "rotateY(-9deg) rotateX(4deg)", transformStyle: "preserve-3d" }}>
              <img src="/img/hero-800.webp" srcSet="/img/hero-800.webp 800w, /img/hero-1600.webp 1600w" sizes="(max-width: 1024px) 100vw, 55vw" width="1600" height="1068" alt="Edificio moderno illuminato di notte da luci rosse e blu" fetchpriority="high" className="aspect-[4/3] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#080A12]/80 via-transparent to-[#075BFF]/20" />
            </div>
            <div data-parallax="-0.08" className="float-a absolute -bottom-10 -left-4 w-[62%] sm:-left-10">
              <div className="glass chrome rounded-2xl p-3 shadow-2xl">
                <img src="/img/casace-logo-400.webp" srcSet="/img/casace-logo-400.webp 400w, /img/casace-logo-640.webp 640w" sizes="(max-width: 640px) 60vw, 340px" width="400" height="193" alt="Logo Casa C'è – Il tuo sì, il tuo sogno, la tua casa." className="w-full rounded-xl" style={{ aspectRatio: "1325 / 640" }} />
              </div>
            </div>
            <div className="float-b glass absolute -right-2 -top-6 hidden items-center gap-3 rounded-2xl px-4 py-3 sm:flex">
              <Network size={18} className="text-[var(--blu)]" />
              <span className="text-xs font-semibold text-white">Chi cerca <span className="text-[var(--rosso)]">↔</span> chi propone</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const PILLARS = [
  { icon: Search, title: "Raccogliamo esigenze", text: "Ascoltiamo chi cerca una casa, un terreno o un'attività e registriamo con cura ogni richiesta." },
  { icon: Megaphone, title: "Promuoviamo opportunità", text: "Presentiamo le proposte dei partner in modo chiaro, dopo una verifica delle informazioni." },
  { icon: Handshake, title: "Creiamo connessioni", text: "Quando c'è una proposta adatta e le condizioni lo consentono, favoriamo il contatto tra le parti." },
];

function About() {
  return (
    <section data-testid="home-about" className="relative py-24 sm:py-32">
      <div className="wrap grid gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow">Chi siamo</p>
          <h2 className="mt-5 text-3xl font-bold leading-tight text-white sm:text-4xl">Un partner commerciale, non un'agenzia immobiliare.</h2>
        </Reveal>
        <Reveal delay={120} className="space-y-5 text-base leading-relaxed muted lg:col-span-7 md:text-lg">
          <p>PUBBLICELLI è un partner commerciale che collabora con aziende, imprese e attività di diversi settori. Creiamo connessioni tra chi dispone di opportunità da valorizzare e chi è alla ricerca della soluzione giusta.</p>
          <p>Attraverso la collaborazione con i nostri partner raccogliamo esigenze, promuoviamo opportunità e aiutiamo le persone a orientarsi tra le proposte disponibili. Le opportunità restano dei soggetti che le propongono: noi le rendiamo visibili e facilitiamo il dialogo.</p>
        </Reveal>
      </div>
      <div className="wrap mt-16 grid gap-5 md:grid-cols-3">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 100}>
            <Tilt className="h-full rounded-2xl">
              <div className="chrome panel h-full rounded-2xl p-7" data-testid={`about-pillar-${i}`}>
                <p.icon className={i === 1 ? "text-[var(--blu)]" : "text-[var(--rosso)]"} size={26} />
                <h3 className="mt-5 text-lg font-semibold text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed muted">{p.text}</p>
              </div>
            </Tilt>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Categories() {
  const { data } = useFetch("/categories");
  const cats = data || [];
  return (
    <section data-testid="home-categories" className="relative py-24">
      <div className="pointer-events-none absolute right-0 top-20 h-96 w-96 glow-blue" />
      <div className="wrap relative">
        <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="eyebrow">Categorie</p>
            <h2 className="mt-5 max-w-2xl text-3xl font-bold leading-tight text-white sm:text-4xl">Cosa puoi trovare tramite i nostri partner</h2>
          </div>
          <Link to="/opportunita" className="btn btn-ghost self-start md:self-auto" data-testid="categories-all-link">Tutte le opportunità<ArrowRight size={15} /></Link>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cats.map((c, i) => {
            const img = imgSources(c.image, c.image_sm);
            return (
              <Reveal key={c.slug} delay={(i % 4) * 80} className={i === 0 || i === 7 ? "lg:col-span-2" : ""}>
                <Tilt max={5} className="group h-full rounded-2xl">
                  <Link to={`/opportunita?categoria=${c.slug}`} data-testid={`category-card-${c.slug}`} className="chrome relative block h-72 overflow-hidden rounded-2xl">
                    {img && <img src={img.src} srcSet={img.srcSet} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" alt="" loading="lazy" decoding="async" className="zoom-img absolute inset-0 h-full w-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#080A12] via-[#080A12]/60 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <h3 className="text-lg font-semibold text-white">{c.name}</h3>
                      <p className="mt-1.5 line-clamp-2 text-sm text-[var(--argento)]">{c.description}</p>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white">
                        Esplora<ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Tilt>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Featured() {
  const { data, loading } = useFetch("/opportunities?sort=numero&limit=24");
  const { data: cats } = useFetch("/categories");
  const catName = (s) => cats?.find((c) => c.slug === s)?.name;
  const items = data?.items || [];
  return (
    <section id="occasioni" data-testid="home-featured" className="scroll-mt-20 py-24">
      <div className="wrap">
        <Reveal>
          <p className="eyebrow">Opportunità da scoprire. Possibilità da realizzare.</p>
          <h2 className="mt-5 text-3xl font-extrabold text-white sm:text-5xl">OCCASIONI <span className="text-gradient">IMPERDIBILI</span></h2>
          <p className="mt-4 max-w-2xl text-sm muted md:text-base">Immobili, terreni e strutture selezionate per chi cerca nuove opportunità. Scopri ogni proposta e richiedi maggiori informazioni.</p>
        </Reveal>
        <div className="mt-12">
          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-96 animate-pulse rounded-2xl bg-white/5" />)}</div>
          ) : items.length ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((o) => <OpportunityCard key={o.id} o={o} categoryName={catName(o.category)} />)}
            </div>
          ) : (
            <EmptyState testid="featured-empty-state" />
          )}
        </div>
      </div>
    </section>
  );
}

function Band({ eyebrow, title, text, cta, to, testid, color = "red", icon: Icon }) {
  return (
    <section data-testid={testid} className="py-12">
      <div className="wrap">
        <Reveal>
          <div className="chrome panel relative overflow-hidden rounded-[28px] p-8 sm:p-14 grain">
            <div className={`pointer-events-none absolute -right-20 -top-20 h-96 w-96 ${color === "red" ? "glow-red" : "glow-blue"}`} />
            <div className={`beam ${color === "red" ? "beam-red" : "beam-blue"} beam-anim fx-only right-[18%]`} />
            <div className="relative grid gap-8 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-8">
                <p className="eyebrow">{eyebrow}</p>
                <h2 className="mt-5 text-2xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl">{title}</h2>
                <p className="mt-5 max-w-2xl text-base leading-relaxed muted">{text}</p>
              </div>
              <div className="flex flex-col items-start gap-5 lg:col-span-4 lg:items-end">
                <Icon size={44} className={color === "red" ? "text-[var(--rosso)]" : "text-[var(--blu)]"} aria-hidden="true" />
                <Link to={to} className={`btn ${color === "red" ? "btn-red" : "btn-blue"} px-7 py-3.5`} data-testid={`${testid}-cta`}>{cta}<ArrowRight size={16} /></Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section data-testid="home-final-cta" className="py-24">
      <div className="wrap grid gap-5 md:grid-cols-2">
        {[
          { t: "Stai cercando qualcosa?", d: "Esplora le opportunità pubblicate o raccontaci cosa desideri trovare.", a: ["Scopri le opportunità", "/opportunita"], b: ["Segnala cosa cerchi", "/segnala-cosa-cerchi"], id: "final-cta-seekers", c: "red" },
          { t: "Sei un'azienda?", d: "Valutiamo insieme come presentare e promuovere le opportunità della tua impresa.", a: ["Diventa partner", "/per-le-aziende#modulo-partner"], b: ["Contattaci", "/contatti"], id: "final-cta-companies", c: "blue" },
        ].map((x, i) => (
          <Reveal key={x.id} delay={i * 120}>
            <div className="chrome panel relative h-full overflow-hidden rounded-3xl p-8 sm:p-10" data-testid={x.id}>
              <div className={`pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 ${x.c === "red" ? "glow-red" : "glow-blue"}`} />
              <h2 className="relative text-2xl font-bold text-white sm:text-3xl">{x.t}</h2>
              <p className="relative mt-3 text-sm muted md:text-base">{x.d}</p>
              <div className="relative mt-8 flex flex-wrap gap-3">
                <Link to={x.a[1]} className={`btn ${x.c === "red" ? "btn-red" : "btn-blue"}`} data-testid={`${x.id}-primary`}>{x.a[0]}</Link>
                <Link to={x.b[1]} className="btn btn-ghost" data-testid={`${x.id}-secondary`}>{x.b[0]}</Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  useSeo({ title: "Le opportunità giuste. Le connessioni che contano.", description: "PUBBLICELLI collabora con aziende e imprese per valorizzare opportunità — case, ville, case al mare, terreni, attività commerciali — e mettere in contatto chi cerca con chi propone.", path: "/" });
  return (
    <>
      <Hero />
      <About />
      <Categories />
      <Featured />
      <Band testid="home-not-found" eyebrow="Non trovi quello che cerchi?" icon={Radar} color="red" to="/segnala-cosa-cerchi" cta="Segnala la tua ricerca"
        title="Dicci cosa stai cercando. Ti aiuteremo a individuare le opportunità adatte."
        text="Indicaci la località, il tipo di immobile o di opportunità e le caratteristiche che desideri. PUBBLICELLI raccoglierà la tua richiesta e verificherà le possibilità disponibili attraverso i propri contatti e partner." />
      <Band testid="home-companies" eyebrow="Per le aziende" icon={Building2} color="blue" to="/per-le-aziende" cta="Diventa partner"
        title="Hai un'azienda o un'attività? Costruiamo nuove opportunità insieme."
        text="Collaboriamo con imprese, attività commerciali e operatori che desiderano valorizzare immobili, proprietà e opportunità, oppure valutare nuove forme di collaborazione commerciale." />
      <FinalCta />
    </>
  );
}
